import { API_BASE_URL, BASE_URL } from "configs/AppConfig";
import { getInformationListFromResponse } from "helpers/informationListFromResponse";

// Static routes (public only, no login required)
// Excluded: login/signup pages (functional, not content), payment result pages (transactional)
const STATIC_ROUTES = [
  { url: "/", lastmod: new Date().toISOString().split("T")[0] },
  { url: "/FAQ", lastmod: new Date().toISOString().split("T")[0] },
  { url: "/contact-us", lastmod: new Date().toISOString().split("T")[0] },
  { url: "/about-us", lastmod: new Date().toISOString().split("T")[0] },
  { url: "/search", lastmod: new Date().toISOString().split("T")[0] },
  { url: "/physical-bidding", lastmod: new Date().toISOString().split("T")[0] },
];

// Get base URL from AppConfig (which reads from environment)
const getBaseUrl = () => {
  return BASE_URL;
};

/**
 * Escape XML special characters
 */
const escapeXml = (unsafe) => {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

/**
 * Format date to YYYY-MM-DD
 */
const formatDate = (date) => {
  if (!date) return new Date().toISOString().split("T")[0];
  return new Date(date).toISOString().split("T")[0];
};

/**
 * Fetch dynamic routes from API endpoints
 */
const fetchDynamicRoutes = async () => {
  const dynamicRoutes = [];

  try {
    // Fetch information pages (same as footer API)
    const informationResponse = await fetch(
      `${API_BASE_URL}information/get/all?type=B2B`
    );

    if (informationResponse.ok) {
      const informationData = await informationResponse.json();
      const informationPages = getInformationListFromResponse(informationData);

      if (Array.isArray(informationPages) && informationPages.length > 0) {
        // More lenient filtering - include all pages that are not explicitly DELETED
        const filteredPages = informationPages.filter((page) => {
          const status = (page.status || "").toLowerCase();
          const isActive =
            status === "active" || !page.status || status !== "deleted";
          return isActive;
        });

        filteredPages.forEach((page) => {
          const pageId = page._id || page.id;
          const lastmod = page.updated_at || page.updatedAt || page.createdAt;

          if (pageId) {
            dynamicRoutes.push({
              url: `/information/${pageId}`,
              lastmod: formatDate(lastmod),
            });
          }
        });
      }
    }
  } catch (error) {
    console.error("Error fetching information pages:", error);
  }

  // Fetch B2C inventories for inventory-detail URLs
  try {
    let page = 1;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const inventoryResponse = await fetch(
        `${API_BASE_URL}b2c-inventory/getAll/public`
      );

      if (!inventoryResponse.ok) break;

      const inventoryData = await inventoryResponse.json();
      const inventories = inventoryData?.data?.data || [];
      const total = inventoryData?.data?.total || 0;

      if (!Array.isArray(inventories) || inventories.length === 0) break;

      inventories.forEach((inventory) => {
        const inventoryId = inventory._id || inventory.id;
        const lastmod =
          inventory.updated_at ||
          inventory.updatedAt ||
          inventory.created_at ||
          inventory.createdAt;

        if (inventoryId) {
          dynamicRoutes.push({
            url: `/inventory-detail/${inventoryId}`,
            lastmod: formatDate(lastmod),
          });
        }
      });

      // Check if there are more pages
      const fetchedSoFar = page * limit;
      hasMore = fetchedSoFar < total;
      page++;
    }
  } catch (error) {
    console.error("Error fetching B2C inventories:", error);
  }

  // Add more API endpoints here as needed
  // Example for other dynamic content:
  /*
  try {
    // Fetch public articles/blogs
    const articlesResponse = await fetch(`${API_BASE_URL}articles/public`);
    if (articlesResponse.ok) {
      const articlesData = await articlesResponse.json();
      const articles = articlesData.data || articlesData;
      
      if (Array.isArray(articles)) {
        articles
          .filter(article => article.status === 'ACTIVE')
          .forEach(article => {
            dynamicRoutes.push({
              url: `/articles/${article._id || article.id}`,
              lastmod: formatDate(article.updated_at || article.updatedAt)
            });
          });
      }
    }
  } catch (error) {
    console.error('Error fetching articles:', error);
  }
  */

  return dynamicRoutes;
};

/**
 * Generate complete sitemap XML
 */
export const generateSitemap = async () => {
  try {
    const baseUrl = getBaseUrl();
    const staticRoutes = STATIC_ROUTES;
    const dynamicRoutes = await fetchDynamicRoutes();

    // Combine all routes
    const allRoutes = [...staticRoutes, ...dynamicRoutes];

    // Generate XML
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';

    allRoutes.forEach((route) => {
      xml += "  <url>\n";
      xml += `    <loc>${escapeXml(baseUrl + route.url)}</loc>\n`;
      xml += `    <lastmod>${route.lastmod}</lastmod>\n`;
      xml += "  </url>\n";
    });

    xml += "</urlset>\n";

    return {
      success: true,
      xml,
      totalUrls: allRoutes.length,
      staticUrls: staticRoutes.length,
      dynamicUrls: dynamicRoutes.length,
      generatedAt: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Error generating sitemap:", error);

    // Fallback to static routes only
    const baseUrl = getBaseUrl();
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';

    STATIC_ROUTES.forEach((route) => {
      xml += "  <url>\n";
      xml += `    <loc>${escapeXml(baseUrl + route.url)}</loc>\n`;
      xml += `    <lastmod>${route.lastmod}</lastmod>\n`;
      xml += "  </url>\n";
    });

    xml += "</urlset>\n";

    return {
      success: false,
      xml,
      totalUrls: STATIC_ROUTES.length,
      staticUrls: STATIC_ROUTES.length,
      dynamicUrls: 0,
      error: error.message,
      generatedAt: new Date().toISOString(),
    };
  }
};

/**
 * Download sitemap as XML file
 */
export const downloadSitemap = async () => {
  try {
    const result = await generateSitemap();

    const blob = new Blob([result.xml], { type: "application/xml" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sitemap.xml";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return result;
  } catch (error) {
    console.error("Error downloading sitemap:", error);
    throw error;
  }
};

export default { generateSitemap, downloadSitemap };
